import { memoize } from "@use/decorators";

import { getStandardPageCount, getTextContent } from "@courses-pm2/api/src/ts/utils";

import { CaptionedContentElementData, CaptionedContentElementModel } from "@courses-pm2/api/src/ts/models/elements/CaptionedContentElementModel";

/**
 * Enumeration of different available targets when opening internal links.
 */
export enum ImageLinkTargets {
    SAME_WINDOW,
    NEW_WINDOW,
    LIGHTBOX,
}

/**
 * Specification of data needed to instantiate a new AccordionModel.
 */
export type ImageData = {
    contain?: boolean;
    disableDefaultDrag?: boolean;
    enableScaleUp?: boolean;
    externalLink?: string;
    internalLink?: string;
    target?: ImageLinkTargets;
    src: string;
    width?: number;
    height?: number;
};

/**
 * Describes the content to be rendered for the element, so that it's made
 * easily available.
 */
export class ImageModel extends CaptionedContentElementModel {
    /**
     * If set to true, then the image component should be contained within the
     * bounding box of it's parent.
     */
    public readonly contain?: boolean;

    /**
     * If set to true, then the image component should disable the default
     * browser image ghost drag functionality, since another implementation
     * of dragging probably will be implemented.
     */
    public readonly disableDefaultDrag?: boolean;

    /**
     * Specifies whether or not to responsively scale the image to fit it's
     * container (this will only prevent upscaling of images, not downscaling to
     * fit within the viewport).
     */
    public readonly enableScaleUp?: boolean;

    /**
     * Optionally specifies an absolute url that should be opened in a new
     * window, when the user clicks the element.
     */
    public readonly externalLink?: string;

    /**
     * Optionally specifies the relative path to a page on the current portal,
     * which should be resolved
     */
    public readonly internalLink?: string;

    /**
     * Specifies whether to open internal links in a new window or in the same
     * window.
     */
    public readonly target?: ImageLinkTargets;

    /**
     * Specifies the absolute url to the image file to be displayed here.
     */
    public readonly src: string;

    /**
     * Optionally contains the width of the image, given in CSS pixels.
     */
    public readonly width?: number;

    /**
     * Optionally contains the height of the image, given in CSS pixels.
     * The desired height of the image
     */
    public readonly height?: number;

    /**
     * Apply data given when a new model is being instantiated.
     */
    constructor(initial: ImageData & CaptionedContentElementData) {
        super(initial);

        this.contain = initial.contain;
        this.disableDefaultDrag = initial.disableDefaultDrag;
        this.enableScaleUp = initial.enableScaleUp;
        this.externalLink = initial.externalLink;
        this.internalLink = initial.internalLink;
        this.target = initial.target;
        this.src = initial.src;
        this.width = initial.width;
        this.height = initial.height;
    }

    /**
     * Simply return the url of the image as the preview image url.
     */
    public async getPreviewImageUrl(): Promise<string> {
        return this.src;
    }

    /**
     * @inheritdoc
     *
     * Due to the nature of the calculation of standard pages, media elements
     * aren't counted on their own. We do however include the caption of the
     * element in the calculations.
     */
    // tslint:disable-next-line: completed-docs
    @memoize()
    public getStandardPageCount(): number {
        return getStandardPageCount(getTextContent(this.getCaptionText()));
    }
}
